import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import UnStyledImage from "./image";

const Wrapper = styled.div.attrs(
  ({
    $align,
    $backgroundColor,
    $textColor,
    $verticalAlign,
    $mediaPosition,
    $imageFill,
    $linkColor,
  }) => ({
    $align: $align,
    $backgroundColor: $backgroundColor || "var(--color-white)",
    $textColor: $textColor || undefined,
    $verticalAlign: $verticalAlign,
    $mediaPosition: $mediaPosition,
    $imageFill: $imageFill,
    $linkColor: $linkColor || undefined,
  })
)`
  ${({ $backgroundColor }) =>
    $backgroundColor &&
    css`
      background-color: ${$backgroundColor};
    `}
  ${({ $textColor }) =>
    $textColor &&
    css`
      color: ${$textColor};
    `}
  ${({ $linkColor }) =>
    $linkColor &&
    css`
      a {
        color: ${$linkColor};
      }
    `}

  display: flex;
  flex-direction: ${({ $mediaPosition }) =>
    $mediaPosition === "left" ? "row" : "row-reverse"};
  align-items: ${({ $verticalAlign }) =>
    $verticalAlign === "top"
      ? "flex-start"
      : $verticalAlign === "bottom"
        ? "flex-end"
        : "center"};
  max-width: ${({ $align }) =>
    $align === "full" ? "100%" : $align === "wide" ? "1600px" : "1180px"};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-wrap: wrap;
    height: auto;
  }
`;

const Image = styled(UnStyledImage).attrs(({ $imageFill, $mediaWidth }) => ({
  $imageFill: $imageFill,
  $mediaWidth: typeof $mediaWidth === "number" ? $mediaWidth : 50,
}))`
  flex-basis: ${({ $mediaWidth }) => `${$mediaWidth}%`};
  height: ${({ $imageFill, $height }) =>
    $imageFill === true ? `${$height}px` : "100%"};
  border-radius: ${({ $className }) =>
    $className === "is-style-rounded" ? "var(--border-radius)" : 0};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-basis: 100%;
    height: ${({ $imageFill }) => ($imageFill ? "40vh" : "auto")};
  }
`;

const Video = styled.video.attrs(({ $mediaWidth }) => ({
  $mediaWidth: typeof $mediaWidth === "number" ? $mediaWidth : 50,
  playsInline: true,
}))`
  flex-basis: ${({ $mediaWidth }) => `${$mediaWidth}%`};
  width: ${({ $mediaWidth }) => `${$mediaWidth}%`};
  object-fit: cover;
  border-radius: ${({ $className }) =>
    $className === "is-style-rounded" ? "var(--border-radius)" : 0};

  height: ${({ $imageFill, $height }) =>
    $imageFill === true ? `${$height}px` : "auto"};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-basis: 100%;
    height: ${({ $imageFill }) => ($imageFill ? "40vh" : "auto")};
  }
`;

const InnerBlock = styled.div.attrs(({ $mediaWidth }) => ({
  $contentWidth: $mediaWidth ? 100 - $mediaWidth : "50%",
}))`
  padding: clamp(0.525rem, 3vw, 1.6rem);
  flex-basis: ${({ $contentWidth }) => `${$contentWidth}%`};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-basis: 100%;
  }
`;

const MediaText = ({
  align,
  backgroundColor,
  media,
  className,
  textColor,
  mediaWidth,
  mediaPosition,
  verticalAlignment,
  imageFill,
  children,
  linkColor,
  aboveFold,
}) => {
  const textBlock = React.useRef(null);
  const [height, setHeight] = React.useState(0);

  React.useEffect(() => {
    const t = setTimeout(() => {
      if (textBlock.current) setHeight(textBlock.current.clientHeight);
    }, 500);
    return () => clearTimeout(t);
  }, []);

  const mime = (media?.mimeType || "").toLowerCase();
  const src =
    media?.localFile?.publicURL ||
    media?.mediaItemUrl ||
    media?.sourceUrl ||
    "";
  const posterSrc =
    media?.poster?.localFile?.publicURL ||
    media?.poster?.mediaItemUrl ||
    media?.poster?.sourceUrl ||
    "";

  const isVideo =
    mime.startsWith("video/") || /\.(mp4|webm|ogg)(\?.*)?$/i.test(src);
  const hasImage = !!(
    media?.localFile?.childImageSharp?.gatsbyImageData || src
  );

  return (
    <Wrapper
      $align={align}
      $backgroundColor={backgroundColor}
      className={className === "is-style-rounded" ? "" : className}
      $textColor={textColor}
      $linkColor={linkColor}
      $mediaPosition={mediaPosition}
      $verticalAlign={verticalAlignment}
      $imageFill={imageFill}
    >
      {isVideo ? (
        src ? (
          <Video
            loop
            controls
            preload="auto"
            poster={posterSrc}
            $mediaWidth={mediaWidth}
            $imageFill={imageFill}
            $height={height}
            $className={className}
          >
            <source src={src} type={media?.mimeType || undefined} />
          </Video>
        ) : null
      ) : hasImage ? (
        <Image
          image={media}
          src={src}
          $mediaWidth={mediaWidth}
          $imageFill={imageFill}
          $height={height}
          $className={className}
          loading={aboveFold ? "eager" : undefined}
          fetchpriority={aboveFold ? "high" : undefined}
        />
      ) : null}

      <InnerBlock $mediaWidth={mediaWidth} ref={textBlock}>
        {children}
      </InnerBlock>
    </Wrapper>
  );
};

MediaText.propTypes = {
  align: PropTypes.string,
  backgroundColor: PropTypes.string,
  media: PropTypes.shape({
    mimeType: PropTypes.string,
    altText: PropTypes.string,
    sourceUrl: PropTypes.string,
    mediaItemUrl: PropTypes.string,
    localFile: PropTypes.shape({
      publicURL: PropTypes.string,
      childImageSharp: PropTypes.shape({
        gatsbyImageData: PropTypes.any,
      }),
    }),
    focalPoint: PropTypes.shape({
      objectPosition: PropTypes.string,
    }),
    poster: PropTypes.shape({
      localFile: PropTypes.shape({
        publicURL: PropTypes.string,
      }),
      sourceUrl: PropTypes.string,
      mediaItemUrl: PropTypes.string,
    }),
  }),
  className: PropTypes.string,
  textColor: PropTypes.string,
  imageFill: PropTypes.bool,
  mediaWidth: PropTypes.number,
  mediaPosition: PropTypes.string,
  verticalAlignment: PropTypes.string,
  children: PropTypes.node,
  linkColor: PropTypes.string,
  aboveFold: PropTypes.bool,
};

export default MediaText;
